<template>
  <div class="friends-module-container dark-theme pa-4 rounded-xl">
    <!-- Premium Header Area -->
    <v-row no-gutters class="mb-4 align-center">
      <v-col cols="12" class="text-center">
        <h1 class="friends-main-title font-weight-black">COMMUNITY & FEED</h1>
        <p class="friends-subtitle text-uppercase">Bleibe verbunden und teile deine Erfolge</p>
      </v-col>
    </v-row>

    <!-- Customized Premium Tab Menu -->
    <div class="pill-tabs-wrapper mb-6">
      <v-tabs v-model="activeTab" dark color="primary" centered hide-slider class="pill-tabs">
        <v-tab key="overview" class="pill-tab-btn px-6 rounded-pill"> <v-icon left size="20">mdi-rss</v-icon> Feed </v-tab>
        <v-tab key="manage" class="pill-tab-btn px-6 rounded-pill">
          <v-icon left size="20">mdi-account-multiple-outline</v-icon> Freunde
          <span class="friendrequest-badge ml-2" v-show="friendrequests.length > 0">
            {{ friendrequests.length }}
          </span>
        </v-tab>
      </v-tabs>
    </div>

    <!-- Tab Contents -->
    <v-tabs-items v-model="activeTab" class="bg-transparent">
      <!-- Tab 1: Unified Social Feed -->
      <v-tab-item key="overview" class="tab-content-item">
        <!-- Empty State with Lottie -->
        <div v-if="filteredFeed.length === 0" class="text-center py-10">
          <lottie-player
            mode="normal"
            style="width: 180px; height: 180px"
            class="ma-auto"
            name="friendlist-empty"
            autoplay
            loop
            src="/img/lottiefiles/friends/friendsPrimaryBubble.json"></lottie-player>
          <h3 class="mt-4 font-weight-bold grey--text text--lighten-2">Momentan nichts los..</h3>
          <p class="grey--text text--lighten-1 subtitle-2 px-6">
            Füge jetzt neue Freunde hinzu und bleibe auf dem Laufenden über ihre Check-ins, Workouts und Kurse!
          </p>
        </div>

        <!-- Feed List -->
        <div v-else class="feed-timeline mx-auto">
          <v-row no-gutters v-for="(entry, i) of filteredFeed" :key="i" class="mb-4">
            <v-card class="feed-post-card rounded-xl elevation-4 w-100 border-dark" outlined>
              <div class="pa-4">
                <!-- Post Header: Avatar, Name & Timestamp -->
                <div class="d-flex align-center mb-3">
                  <div class="avatar-wrapper mr-3">
                    <v-img
                      v-if="entry.user && entry.user.bild"
                      class="friend-avatar rounded-circle border-avatar"
                      :src="'/img/profilbilder' + entry.user.bild"
                      width="48"
                      height="48" />
                    <v-img
                      v-else
                      class="friend-avatar rounded-circle border-avatar"
                      src="/img/profilbilder/uploads/1605087902456roodytest.png"
                      width="48"
                      height="48" />
                  </div>
                  <div class="flex-grow-1">
                    <div class="font-weight-bold text-subtitle-1 white--text line-height-tight">
                      {{ entry.user ? entry.user.vorname + " " + entry.user.nachname : "Ein Mitglied" }}
                    </div>
                    <div class="post-time text-caption d-flex align-center mt-1 text-secondary">
                      <v-icon size="12" class="mr-1" color="grey lighten-1">mdi-clock-outline</v-icon>
                      {{ formatPostTime(entry.ts) }}
                    </div>
                  </div>

                  <!-- Category Icon Badge -->
                  <div class="category-badge-wrapper">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon
                          v-bind="attrs"
                          v-on="on"
                          :color="getCategoryColor(entry.type)"
                          size="24"
                          class="category-icon pa-2 rounded-circle"
                          :style="{ backgroundColor: getCategoryBg(entry.type) }">
                          {{ getCategoryIcon(entry.type) }}
                        </v-icon>
                      </template>
                      <span>{{ getCategoryLabel(entry.type) }}</span>
                    </v-tooltip>
                  </div>
                </div>

                <!-- Post Content Wrapper -->
                <div class="post-content pa-1">
                  <!-- A. Group Fitness Signup -->
                  <div v-if="entry.type === 'groupfitness'" class="groupfitness-post-content rounded-lg pa-3 bg-card-inner border-gf">
                    <div class="d-flex align-center">
                      <div class="flex-grow-1">
                        <span class="activity-tag tag-gf rounded px-2 py-1 text-uppercase font-weight-bold mr-2 mb-2 d-inline-block"
                          >Group Fitness</span
                        >
                        <h4 class="font-weight-black text-h6 white--text mb-1">{{ entry.course }}</h4>
                        <p class="mb-0 text-body-2 text-secondary">
                          <v-icon size="16" class="mr-1" color="green lighten-1">mdi-calendar-check</v-icon>
                          Kurs gebucht für <strong>{{ entry.time }}</strong>
                        </p>
                      </div>
                      <v-avatar v-if="entry.courseImage" size="64" class="rounded-lg ml-2 border-gf-img elevation-1">
                        <v-img :src="'/img/kurse/' + entry.courseImage"></v-img>
                      </v-avatar>
                    </div>
                  </div>

                  <!-- B. Completed Workout (V1 & V2) -->
                  <div v-else-if="entry.type === 'workout'" class="workout-post-content rounded-lg pa-3 bg-card-inner border-workout">
                    <div class="d-flex align-center justify-space-between mb-2">
                      <div>
                        <span class="activity-tag tag-workout rounded px-2 py-1 text-uppercase font-weight-bold mr-2 mb-2 d-inline-block"
                          >Workout</span
                        >
                        <h4 class="font-weight-black text-h6 white--text mb-1">{{ entry.title }}</h4>
                      </div>
                      <!-- Workout visual icon / SVG wrapper -->
                      <v-avatar v-if="entry.image" size="56" class="rounded-lg border-workout-img elevation-1 bg-dark-card">
                        <v-img
                          :src="
                            entry.image.startsWith('/img') || entry.image.startsWith('data:') ? entry.image : '/img/uebungen/' + entry.image
                          "></v-img>
                      </v-avatar>
                    </div>

                    <!-- Decent Grid stats -->
                    <div class="workout-stats-grid mt-2">
                      <div v-if="entry.duration" class="workout-stat-item d-flex align-center pa-2 rounded-lg bg-dark-stat border-stat">
                        <v-icon color="orange lighten-1" size="18" class="mr-2">mdi-timer-outline</v-icon>
                        <div class="d-flex flex-column">
                          <span class="text-caption text-secondary">Dauer</span>
                          <span class="font-weight-bold text-subtitle-2 white--text">{{ formatDuration(entry.duration) }}</span>
                        </div>
                      </div>

                      <div v-if="entry.caloriesBurned" class="workout-stat-item d-flex align-center pa-2 rounded-lg bg-dark-stat border-stat">
                        <v-icon color="red lighten-1" size="18" class="mr-2">mdi-fire</v-icon>
                        <div class="d-flex flex-column">
                          <span class="text-caption text-secondary">Kalorien</span>
                          <span class="font-weight-bold text-subtitle-2 white--text">{{ entry.caloriesBurned }} kcal</span>
                        </div>
                      </div>

                      <div
                        v-if="entry.liftedWeight && Number(entry.liftedWeight) > 0"
                        class="workout-stat-item d-flex align-center pa-2 rounded-lg bg-dark-stat border-stat">
                        <v-icon color="blue-grey lighten-2" size="18" class="mr-2">mdi-dumbbell</v-icon>
                        <div class="d-flex flex-column">
                          <span class="text-caption text-secondary">Gewicht</span>
                          <span class="font-weight-bold text-subtitle-2 white--text">{{ entry.liftedWeight }} kg</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- C. Gym Check-in / Attendance -->
                  <div v-else-if="entry.type === 'checkin'" class="checkin-post-content rounded-lg pa-3 bg-card-inner border-checkin">
                    <span class="activity-tag tag-checkin rounded px-2 py-1 text-uppercase font-weight-bold mr-2 mb-2 d-inline-block">Check-in</span>
                    <h4 class="font-weight-black text-subtitle-1 white--text mb-1">Erfolgreicher Check-in im Studio!</h4>
                    <p class="mb-2 text-body-2 text-secondary">
                      <v-icon size="16" class="mr-1" color="indigo lighten-2">mdi-login-variant</v-icon>
                      Eingetroffen um <strong>{{ entry.time }} Uhr</strong>
                      <span v-if="entry.checkoutTime">
                        <br />
                        <v-icon size="16" class="mr-1 mt-1" color="grey lighten-1">mdi-logout-variant</v-icon>
                        Ausgecheckt um <strong>{{ entry.checkoutTime }} Uhr</strong>
                      </span>
                    </p>

                    <div v-if="entry.duration" class="checkin-duration-badge d-inline-flex align-center px-3 py-1 rounded-pill mt-1">
                      <v-icon size="14" color="indigo lighten-2" class="mr-1">mdi-clock-check-outline</v-icon>
                      <span class="font-weight-bold indigo--text text--lighten-2 text-caption">Trainingzeit: {{ entry.duration }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </v-card>
          </v-row>
        </div>
      </v-tab-item>

      <!-- Tab 2: Friends Management & Privacy Controls -->
      <v-tab-item key="manage" class="tab-content-item">
        <v-row no-gutters>
          <!-- Left Column: Add Friend & Privacy Settings -->
          <v-col cols="12" md="6" class="pr-md-4">
            <!-- Section A: Add Friend -->
            <v-card class="friends-inner-card rounded-xl pa-4 mb-5 border-dark" outlined>
              <h3 class="font-weight-black white--text mb-2 d-flex align-center">
                <v-icon color="primary" class="mr-2">mdi-account-plus-outline</v-icon>
                Freund hinzufügen
              </h3>
              <p class="text-caption text-secondary mb-4">
                Gib die E-Mail-Adresse deines Freundes ein, um ihm eine Freundschaftsanfrage zu schicken.
              </p>

              <v-row no-gutters align="center" no-gutters>
                <v-col class="flex-grow-1 pr-2">
                  <v-text-field
                    v-model="mailfr"
                    placeholder="roody@roody.ch"
                    label="E-Mail Adresse"
                    outlined
                    dark
                    dense
                    hide-details
                    class="rounded-lg modern-text-field"></v-text-field>
                </v-col>
                <v-col class="flex-shrink-0">
                  <v-btn @click="addFriend()" color="primary" depressed fab small class="rounded-lg add-friend-fab-btn">
                    <v-icon>mdi-send-outline</v-icon>
                  </v-btn>
                </v-col>
              </v-row>
            </v-card>

            <!-- Section B: Privacy & Sharing Preferences -->
            <v-card class="friends-inner-card rounded-xl pa-4 mb-5 border-dark" outlined>
              <h3 class="font-weight-black white--text mb-2 d-flex align-center">
                <v-icon color="indigo lighten-2" class="mr-2">mdi-shield-lock-outline</v-icon>
                Meine Privatsphäre
              </h3>
              <p class="text-caption text-secondary mb-4">Bestimme selbst, welche Aktivitäten du mit deinen Freunden teilen möchtest.</p>

              <!-- Preference switches list -->
              <div class="privacy-settings-list">
                <!-- Toggle 1: Group Fitness (Default true) -->
                <div class="privacy-setting-item d-flex align-center justify-space-between py-2 border-bottom-subtle">
                  <div class="pr-4">
                    <span class="font-weight-bold d-block text-subtitle-2 white--text">Group Fitness Kurse teilen</span>
                    <span class="text-caption text-secondary">Teile deine Kursanmeldungen und absolvierten GF-Stunden.</span>
                  </div>
                  <v-switch
                    v-model="sharingPrefs.shareGroupFitness"
                    @change="updateSharingPreference"
                    color="primary"
                    dark
                    hide-details
                    class="mt-0"></v-switch>
                </div>

                <!-- Toggle 2: Workouts (Default false) -->
                <div class="privacy-setting-item d-flex align-center justify-space-between py-2 border-bottom-subtle">
                  <div class="pr-4">
                    <span class="font-weight-bold d-block text-subtitle-2 white--text">Trainingspläne teilen</span>
                    <span class="text-caption text-secondary">Teile abgeschlossene Workouts, Split-Erfolge, Dauer & Kalorien.</span>
                  </div>
                  <v-switch
                    v-model="sharingPrefs.shareWorkouts"
                    @change="updateSharingPreference"
                    color="primary"
                    dark
                    hide-details
                    class="mt-0"></v-switch>
                </div>

                <!-- Toggle 3: Checkins (Default false) -->
                <div class="privacy-setting-item d-flex align-center justify-space-between py-2">
                  <div class="pr-4">
                    <span class="font-weight-bold d-block text-subtitle-2 white--text">Check-ins teilen</span>
                    <span class="text-caption text-secondary">Teile, wann du im Studio eincheckst und wie lange du trainierst.</span>
                  </div>
                  <v-switch
                    v-model="sharingPrefs.shareCheckins"
                    @change="updateSharingPreference"
                    color="primary"
                    dark
                    hide-details
                    class="mt-0"></v-switch>
                </div>
              </div>
            </v-card>
          </v-col>

          <!-- Right Column: Friend Requests & Friendlist -->
          <v-col cols="12" md="6" class="pl-md-4">
            <!-- Friend Requests Section -->
            <div v-if="friendrequests.length > 0" class="mb-5">
              <h3 class="font-weight-black white--text mb-3 d-flex align-center">
                <v-icon color="orange" class="mr-2">mdi-account-clock-outline</v-icon>
                Freundschaftsanfragen
              </h3>
              <v-row no-gutters>
                <v-col v-for="(fr, i) of friendrequests" :key="i" cols="12">
                  <v-card class="friend-card rounded-xl pa-3 d-flex align-center border-dark bg-dark-card" outlined>
                    <v-avatar v-if="fr.user.bild" size="48" class="mr-3 border-avatar">
                      <v-img :src="'/img/profilbilder' + fr.user.bild"></v-img>
                    </v-avatar>
                    <v-avatar v-else size="48" class="mr-3 border-avatar bg-grey darken-3">
                      <v-icon color="grey lighten-1">mdi-account</v-icon>
                    </v-avatar>

                    <div class="flex-grow-1">
                      <div class="font-weight-bold text-subtitle-2 white--text">{{ fr.user.name }}</div>
                      <div class="text-caption text-secondary text-truncate" style="max-width: 180px">{{ fr.user.mail }}</div>
                    </div>

                    <div class="d-flex align-center">
                      <v-btn @click="acceptFriend(fr.request._id)" color="success" icon small class="mx-1 bg-success-trans rounded-lg">
                        <v-icon size="20">mdi-check</v-icon>
                      </v-btn>
                      <v-btn @click="declineFriendRequest(fr.request._id)" color="error" icon small class="mx-1 bg-error-trans rounded-lg">
                        <v-icon size="20">mdi-close</v-icon>
                      </v-btn>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </div>

            <!-- Friendlist Section -->
            <div>
              <h3 class="font-weight-black white--text mb-3 d-flex align-center">
                <v-icon color="primary" class="mr-2">mdi-card-account-details-outline</v-icon>
                Meine Freunde ({{ friendlist.length }})
              </h3>

              <!-- Empty list message -->
              <div v-if="friendlist.length === 0" class="text-center py-6 border-dashed rounded-xl bg-dark-card">
                <v-icon color="grey lighten-1" size="40" class="mb-2">mdi-account-question-outline</v-icon>
                <p class="mb-0 text-subtitle-2 text-secondary">Du hast noch keine Freunde hinzugefügt.</p>
              </div>

              <!-- List -->
              <v-row no-gutters v-else style="margin-bottom: 120px">
                <v-col v-for="(fr, i) of friendlist" :key="i" cols="12">
                  <v-card class="friend-card rounded-xl pa-3 d-flex align-center border-dark bg-dark-card" outlined>
                    <v-avatar v-if="fr.user.bild" size="48" class="mr-3 border-avatar">
                      <v-img :src="'/img/profilbilder' + fr.user.bild"></v-img>
                    </v-avatar>
                    <v-avatar v-else size="48" class="mr-3 border-avatar bg-grey darken-3">
                      <v-icon color="grey lighten-1">mdi-account</v-icon>
                    </v-avatar>

                    <div class="flex-grow-1">
                      <div class="font-weight-bold text-subtitle-2 white--text">{{ fr.user.vorname }} {{ fr.user.nachname }}</div>
                      <div class="text-caption text-secondary text-truncate" style="max-width: 180px">{{ fr.user.email }}</div>
                    </div>

                    <div>
                      <v-btn @click="deleteFriend(fr.request_id)" color="error" icon small class="bg-error-trans rounded-lg">
                        <v-icon size="20">mdi-delete-outline</v-icon>
                      </v-btn>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </div>
          </v-col>
        </v-row>
      </v-tab-item>
    </v-tabs-items>
  </div>
</template>

<script>
import api from "@/api"
import userService from "@/services/userService"

export default {
  name: "FriendsModule",
  data() {
    return {
      userService,
      activeTab: null,
      friendlist: [],
      friendrequests: [],
      feed: [],
      mailfr: "",
      sharingPrefs: {
        shareWorkouts: false,
        shareGroupFitness: true,
        shareCheckins: false,
      },
    }
  },
  methods: {
    formatPostTime(ts) {
      if (!ts) return ""
      const date = this.$moment.unix(ts)
      const today = this.$moment().startOf("day")
      const yesterday = this.$moment().subtract(1, "days").startOf("day")

      if (date.isSame(today, "d")) {
        return "Heute um " + date.format("HH:mm") + " Uhr"
      } else if (date.isSame(yesterday, "d")) {
        return "Gestern um " + date.format("HH:mm") + " Uhr"
      } else {
        return date.format("DD.MM.YYYY [um] HH:mm") + " Uhr"
      }
    },
    formatDuration(duration) {
      if (!duration) return ""
      if (typeof duration === "string" && duration.includes(":")) {
        const parts = duration.split(":")
        if (parts.length === 3) {
          const hrs = parseInt(parts[0], 10)
          const mins = parseInt(parts[1], 10)
          if (hrs > 0) return `${hrs} Std. ${mins} Min.`
          return `${mins} Min.`
        }
        return duration
      }
      return duration + " Min."
    },
    getCategoryIcon(type) {
      switch (type) {
        case "groupfitness":
          return "mdi-calendar-multiselect"
        case "workout":
          return "mdi-dumbbell"
        case "checkin":
          return "mdi-map-marker-check"
        default:
          return "mdi-star-outline"
      }
    },
    getCategoryColor(type) {
      switch (type) {
        case "groupfitness":
          return "green lighten-2"
        case "workout":
          return "orange lighten-2"
        case "checkin":
          return "indigo lighten-2"
        default:
          return "grey lighten-1"
      }
    },
    getCategoryBg(type) {
      switch (type) {
        case "groupfitness":
          return "rgba(76, 175, 80, 0.15)"
        case "workout":
          return "rgba(255, 152, 0, 0.15)"
        case "checkin":
          return "rgba(63, 81, 181, 0.18)"
        default:
          return "rgba(255, 255, 255, 0.08)"
      }
    },
    getCategoryLabel(type) {
      switch (type) {
        case "groupfitness":
          return "Group Fitness"
        case "workout":
          return "Workout"
        case "checkin":
          return "Check-in"
        default:
          return "Aktivität"
      }
    },
    acceptFriend(id) {
      api
        .acceptFriend(id)
        .then((data) => {
          if (data.status == 1) {
            this.$toast?.success("Freund erfolgreich hinzugefügt") || alert("Freund hinzugefügt")
            this.getFriendsFeed()
            this.getFriendrequests()
            if (
              window.app &&
              window.app.$root &&
              window.app.$root.$children[0] &&
              typeof window.app.$root.$children[0].loadFriendrequests === "function"
            ) {
              window.app.$root.$children[0].loadFriendrequests()
            }
          }
        })
        .catch((error) => console.error("Error accepting friend:", error))
    },
    addFriend() {
      if (this.mailfr.trim() !== "") {
        api
          .addFriend(this.mailfr.trim())
          .then((data) => {
            if (data.status == 1) {
              this.$toast?.success("Freundschaftsanfrage verschickt") || alert("Freundschaftsanfrage verschickt")
              this.mailfr = ""
            } else if (data.status == 2) {
              this.$toast?.info("Freund wurde bereits angefragt") || alert("Freund wurde bereits angefragt")
              this.mailfr = ""
            } else {
              this.$toast?.error("Benutzer nicht gefunden") || alert("Benutzer nicht gefunden")
            }
          })
          .catch((error) => console.error("Error adding friend:", error))
      } else {
        this.$toast?.warning("Bitte gib eine gültige E-Mail-Adresse ein") || alert("Bitte gib eine gültige E-Mail-Adresse ein")
      }
    },
    declineFriendRequest(id) {
      api
        .removeFriend(id)
        .then((data) => {
          if (data.status == 1) {
            this.$toast?.info("Freundschaftsanfrage abgelehnt") || alert("Freundschaftsanfrage abgelehnt")
            this.getFriendrequests()
          }
        })
        .catch((error) => console.error("Error declining friend request:", error))
    },
    deleteFriend(id) {
      if (confirm("Freund wirklich entfernen?")) {
        api
          .removeFriend(id)
          .then((data) => {
            if (data.status == 1) {
              this.$toast?.success("Freund entfernt") || alert("Freund entfernt")
              this.getFriendrequests()
            }
          })
          .catch((error) => console.error("Error deleting friend:", error))
      }
    },
    async getFriends() {
      try {
        let data = await api.getFriends()
        this.friendlist = []
        if (data && data.freunde) {
          for (let freund of data.freunde) {
            if (freund.user) {
              this.friendlist.push({
                user: freund.user,
                request_id: freund.request_id,
              })
            }
          }
        }
        this.getFriendsFeed()
      } catch (e) {
        console.error("Error getting friends:", e)
      }
    },
    async getFriendrequests() {
      try {
        this.friendrequests = await api.getFriendrequests()
        await this.getFriends()
      } catch (e) {
        console.error("Error getting requests:", e)
      }
    },
    async getFriendsFeed() {
      try {
        this.feed = await api.getFriendsFeed()
      } catch (e) {
        console.error("Error loading feed:", e)
      }
    },
    async getSharingPreferences() {
      try {
        const prefs = await api.getSharingPreferences()
        if (prefs) {
          this.sharingPrefs = {
            shareWorkouts: prefs.shareWorkouts === true,
            shareGroupFitness: prefs.shareGroupFitness !== false,
            shareCheckins: prefs.shareCheckins === true,
          }
        }
      } catch (e) {
        console.error("Error loading privacy preferences:", e)
      }
    },
    async updateSharingPreference() {
      try {
        await api.saveSharingPreferences(this.sharingPrefs)
        this.$toast?.success("Privatsphäre-Einstellungen aktualisiert") || console.log("Sharing preferences updated")
      } catch (e) {
        console.error("Error saving privacy preferences:", e)
        this.$toast?.error("Fehler beim Speichern der Einstellungen") || alert("Fehler beim Speichern")
      }
    },
  },
  computed: {
    filteredFeed() {
      return this.feed
    },
  },
  async created() {
    await this.getSharingPreferences()
    await this.getFriendrequests()
  },
}
</script>

<style lang="scss" scoped>
@import url("https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800;900&display=swap");

.friends-module-container {
  font-family: "Poppins", sans-serif;
  max-width: 768px;
  margin: 0 auto;

  /* Pure Neutral Dark Theme (Matches Reservations screen, absolutely zero blue tint) */
  background-color: #121212 !important;
  color: #ffffff !important;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.6) !important;
  border: none !important;
}

.friends-main-title {
  font-size: 2.2rem;
  letter-spacing: -0.5px;
  background: linear-gradient(135deg, #ffffff 0%, #ebebef 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 2px;
}

.friends-subtitle {
  font-size: 0.75rem;
  color: #8a8a8f;
  letter-spacing: 2px;
  font-weight: 600;
}

.text-secondary {
  color: #8a8a8f !important;
}

/* Custom Pill-Tabs Styling (Matches screen 2: Pure dark layout with absolute zero white bar) */
.pill-tabs-wrapper {
  background: #1c1c1e;
  padding: 6px;
  border-radius: 12px; /* Matches reservation cards border-radius */
  display: inline-block;
  width: 100%;
  text-align: center;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.4);
  border: 1px solid #2c2c2e;
}

.pill-tabs {
  background-color: transparent !important;

  .pill-tab-btn {
    min-height: 40px;
    height: 40px;
    text-transform: none;
    font-weight: 600;
    font-size: 0.95rem;
    color: #8a8a8f;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    letter-spacing: 0;

    &.v-tab--active {
      background-color: #2c2c2e !important;
      color: #52ae32 !important; /* Signature green text for active tab */
      box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
    }
  }
}

.friendrequest-badge {
  background-color: #ef4444;
  color: #ffffff;
  font-size: 0.7rem;
  font-weight: 700;
  border-radius: 999px;
  min-width: 18px;
  height: 18px;
  line-height: 18px;
  display: inline-block;
  text-align: center;
  padding: 0 4px;
}

/* Timeline post cards styles (Pure dark neutral) */
.feed-timeline {
  max-width: 600px;
}

.feed-post-card {
  background: #1c1c1e !important;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  overflow: hidden;
  border: 1px solid #2c2c2e !important;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 24px rgba(0, 0, 0, 0.5) !important;
  }
}

.border-dark {
  border: 1px solid #2c2c2e !important;
}

.border-avatar {
  border: 2px solid #2c2c2e;
}

.avatar-wrapper {
  position: relative;
}

.line-height-tight {
  line-height: 1.25;
}

/* Category icons and badges */
.category-icon {
  width: 40px;
  height: 40px;
  display: flex !important;
  justify-content: center;
  align-items: center;
  box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.2);
}

/* Neutralized content containers (Eliminated blue tint) */
.bg-card-inner {
  background-color: #2c2c2e !important;
}

.activity-tag {
  font-size: 0.65rem;
  letter-spacing: 0.5px;
}

.tag-gf {
  background-color: rgba(76, 175, 80, 0.12);
  color: #81c784;
}

.tag-workout {
  background-color: rgba(255, 152, 0, 0.12);
  color: #ffb74d;
}

.tag-checkin {
  background-color: rgba(63, 81, 181, 0.12);
  color: #9fa8da;
}

.border-gf {
  border: 1px solid rgba(76, 175, 80, 0.2);
}

.border-gf-img {
  border: 2px solid #1c1c1e;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.4);
}

.border-workout {
  border: 1px solid rgba(255, 152, 0, 0.2);
}

.border-workout-img {
  border: 2px solid #1c1c1e;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.4);
}

.border-checkin {
  border: 1px solid rgba(63, 81, 181, 0.2);
}

.checkin-duration-badge {
  background-color: rgba(63, 81, 181, 0.15);
  border: 1px solid rgba(63, 81, 181, 0.3);
}

/* Workout Stats decent grid */
.workout-stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(130px, 1fr));
  gap: 12px;
}

.workout-stat-item {
  background-color: #1c1c1e !important;
}

.border-stat {
  border: 1px solid #2c2c2e;
}

/* Inner card styles for management */
.friends-inner-card {
  background: #1c1c1e !important;
  border: 1px solid #2c2c2e !important;
}

/* Dark Mode Form Inputs (Pure Dark Style) */
.modern-text-field {
  ::v-deep .v-input__control {
    background-color: #121212 !important;
    border-radius: 8px !important;
  }
  ::v-deep input {
    color: #ffffff !important;
  }
  ::v-deep label {
    color: #8a8a8f !important;
  }
  ::v-deep .v-input__slot {
    border: 1px solid #2c2c2e !important;
    &:hover {
      border-color: #3a3a3c !important;
    }
  }
}

.add-friend-fab-btn {
  box-shadow: 0 4px 10px rgba(82, 174, 50, 0.2) !important;
  color: #ffffff !important;
}

/* Privacy Settings switches item styling */
.privacy-settings-list {
  display: flex;
  flex-direction: column;
}

.privacy-setting-item {
  display: flex;
  align-items: center;
  justify-content: space-between;

  ::v-deep .v-label {
    color: #ffffff !important;
  }
}

.border-bottom-subtle {
  border-bottom: 1px solid #2c2c2e;
}

/* Friends list / requests card styling */
.friend-card {
  background: #1c1c1e !important;
  border: 1px solid #2c2c2e !important;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
    border-color: #3a3a3c !important;
  }
}

.bg-success-trans {
  background-color: rgba(76, 175, 80, 0.15) !important;
  color: #81c784 !important;
  &:hover {
    background-color: rgba(76, 175, 80, 0.25) !important;
  }
}

.bg-error-trans {
  background-color: rgba(244, 67, 54, 0.15) !important;
  color: #e57373 !important;
  &:hover {
    background-color: rgba(244, 67, 54, 0.25) !important;
  }
}

.border-dashed {
  border: 2px dashed #2c2c2e;
}

.bg-dark-card {
  background-color: #1c1c1e !important;
}

.bg-dark-stat {
  background-color: #1c1c1e !important;
}

.dark-btn-outlined {
  border-color: #2c2c2e !important;
  color: #52ae32 !important;
  background-color: #1c1c1e !important;
  &:hover {
    background-color: #2c2c2e !important;
  }
}
</style>
